import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { showNotification } from "@mantine/notifications";
import { customAxios } from "lib/axios";

export type HourlyLeavePayload = {
  leaveType: string;
  reason?: string;
  date: string;
  hours: number;
  departmentId?: string;
  convertToUnpaid?: boolean;
};

export interface Leave {
  _id: string;
  leaveType: string;
  reason?: string;
  date: string;
  hours: number;
  departmentId?: string;
  convertToUnpaid?: boolean;
  status?: string;
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

export type HourlyLeaveResponse = {
  success: boolean;
  message: string;
  data: Leave;
};

const updateHourlyLeaveRequest = async (
  id: string,
  payload: HourlyLeavePayload
): Promise<HourlyLeaveResponse> => {
  const { data } = await customAxios.put<HourlyLeaveResponse>(
    `/leaves/put-hourly-leave-request/${id}`,
    payload
  );
  return data;
};

export const useUpdateHourlyLeaveRequest = () => {
  const queryClient = useQueryClient();

  return useMutation<
    HourlyLeaveResponse,
    AxiosError,
    { id: string; payload: HourlyLeavePayload }
  >(({ id, payload }) => updateHourlyLeaveRequest(id, payload), {
    onSuccess: async (data, variables) => {
      showNotification({
        title: "Success",
        message: data.message ?? "Hourly leave updated",
        color: "green",
      });

      await queryClient.invalidateQueries(["leaves"]);
      await queryClient.invalidateQueries(["leave", variables.id]);
    },
    onError: (error) =>
      showNotification({
        title: "Error",
        message:
          (error.response?.data as { message?: string })?.message ??
          "Failed to update hourly leave",
        color: "red",
      }),
  });
};
